import React, { useState, useMemo } from 'react';
import { Badge } from '@/race-tracker/components/ui/badge';
import { Input } from '@/race-tracker/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/race-tracker/components/ui/select';
import { Search, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { useChangeLogs } from '@/big-political-list/hooks/useBplData';
import BplQueryError from '@/big-political-list/components/shared/BplQueryError';
import { BplLogListSkeleton, BplUpdatingBanner } from '@/big-political-list/components/shared/BplSkeleton';
import { format } from 'date-fns';

const ACTION_COLORS = {
  created: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  updated: 'bg-blue-100 text-blue-700 border-blue-200',
  deleted: 'bg-red-100 text-red-700 border-red-200',
  imported: 'bg-purple-100 text-purple-700 border-purple-200',
};

const ENTITY_TYPE_LABELS = {
  Station: 'Station',
  RepFirmManager: 'Rep Firm Manager',
  StationShare: 'Station Share',
  Both: 'Both',
};

function entityTypeLabel(entityType) {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

export default function ChangeLog() {
  const { data: logs = [], isLoading, isFetching, isError, error, refetch } = useChangeLogs();
  const showInitialSkeleton = isLoading && logs.length === 0;
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());

  const filtered = useMemo(() => {
    return logs.filter(l => {
      if (actionFilter && l.action !== actionFilter) return false;
      if (entityFilter && l.entity_type !== entityFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(l.record_label || '').toLowerCase().includes(q) &&
            !(l.user || '').toLowerCase().includes(q) &&
            !(l.summary || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [logs, actionFilter, entityFilter, search]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <BplUpdatingBanner
        message={
          showInitialSkeleton
            ? 'Loading change log…'
            : isFetching && logs.length > 0
              ? 'Refreshing change log…'
              : null
        }
      />
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search logs…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card" />
        </div>
        <Select value={actionFilter || '_all_'} onValueChange={v => setActionFilter(v === '_all_' ? '' : v)}>
          <SelectTrigger className="w-36 h-9 text-xs bg-card"><SelectValue placeholder="All Actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all_">All Actions</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="updated">Updated</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
            <SelectItem value="imported">Imported</SelectItem>
          </SelectContent>
        </Select>
        <Select value={entityFilter || '_all_'} onValueChange={v => setEntityFilter(v === '_all_' ? '' : v)}>
          <SelectTrigger className="w-40 h-9 text-xs bg-card"><SelectValue placeholder="All Entities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all_">All Entities</SelectItem>
            <SelectItem value="Station">Station</SelectItem>
            <SelectItem value="RepFirmManager">Rep Firm Manager</SelectItem>
            <SelectItem value="StationShare">Station Share</SelectItem>
            <SelectItem value="Both">Both</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isError && logs.length === 0 ? (
        <BplQueryError label="change log" error={error} onRetry={refetch} />
      ) : showInitialSkeleton ? (
        <BplLogListSkeleton rows={8} />
      ) : (
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">No change log entries found.</div>
        )}
        {filtered.map(entry => {
          const hasChanges = entry.action === 'updated' && entry.field_changes?.length > 0;
          const isExpanded = expandedIds.has(entry.id);

          return (
            <div key={entry.id} className="rounded-lg border bg-card overflow-hidden">
              <button
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/30 transition-colors"
                onClick={() => hasChanges && toggleExpand(entry.id)}
              >
                {hasChanges ? (
                  isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                ) : <div className="w-4 shrink-0" />}

                <Badge variant="outline" className={`text-[10px] shrink-0 ${ACTION_COLORS[entry.action] || ''}`}>
                  {entry.action}
                </Badge>

                <Badge variant="secondary" className="text-[10px] shrink-0">{entityTypeLabel(entry.entity_type)}</Badge>

                <span className="text-sm font-medium truncate flex-1">{entry.record_label || entry.summary || '—'}</span>

                <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                  <span>{entry.user}</span>
                  <span>{entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a') : format(new Date(entry.created_date), 'MMM d, yyyy h:mm a')}</span>
                </div>
              </button>

              {/* Expanded field changes */}
              {hasChanges && isExpanded && (
                <div className="px-4 pb-3 pt-1 border-t bg-muted/20">
                  <div className="space-y-1.5">
                    {entry.field_changes.map((ch, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-foreground w-32 shrink-0">{ch.field}</span>
                        <span className="text-red-600 line-through truncate max-w-[200px]">{ch.old_value || '(empty)'}</span>
                        <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-emerald-600 truncate max-w-[200px]">{ch.new_value || '(empty)'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {entry.action === 'imported' && entry.summary && (
                <div className="px-4 pb-3 pt-1 border-t bg-muted/20">
                  <p className="text-xs text-muted-foreground">{entry.summary}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}